import cn from 'classnames';
import { useCallback, useContext } from 'react';

import { UserContext, VoucherContext, BasketContext } from 'components/signUpFunnel/Contexts';
import InternalSkipButton from 'components/signUpFunnel/InternalSkip/InternalSkipButton.js';
import { TEMPO } from 'context/BrandContext';
import { getEnvConstants } from 'helpers/envConstants';
import {
  generateRandomName,
  generateChicagoStreetAddress,
  generateChicagoPhoneNumber,
  parseNameFromEmail,
  generateFutureDeliveryDate,
} from 'helpers/internalSkipFunctions';
import useInternalSkip from 'hooks/useInternalSkip';
import { STANDARD, FAMILY } from 'lib/constants';
import { Statuses, SKIP_PATHS } from 'lib/constants/signUp';
import * as ErrorLogging from 'services/ErrorLogging';
import RequestService from 'services/RequestService';

const SkipContainer = ({ history, introSubmitParams, setError, setIsSubmitting, mealPlan }) => {
  const { RUNTIME_ENV } = getEnvConstants();
  if (RUNTIME_ENV === 'production') return false;

  const { internalValues, handlePath, setIsInternalVisible, isInternalVisible } =
    useInternalSkip(mealPlan);
  const { user, setUser } = useContext(UserContext);
  const { setVoucher } = useContext(VoucherContext);
  const { setBasket } = useContext(BasketContext);

  const isTempo = mealPlan === TEMPO;
  const isHomeChef = mealPlan === STANDARD;
  const isFamilyPlan = mealPlan === FAMILY;

  const preferencesFunnelPage = {
    page: 'preferences',
    path: SKIP_PATHS.PREF_DIETARY,
    status: Statuses.DIETARY_PREFERENCES_STEP,
  };

  const funnelPages = [
    preferencesFunnelPage,
    ...(!isFamilyPlan
      ? [
          {
            page: 'promo',
            path: SKIP_PATHS.PROMO,
            status: Statuses.PROMO_STEP,
          },
        ]
      : []),

    {
      page: 'box people',
      path: SKIP_PATHS.BOX_PEOPLE,
      status: Statuses.BOX_PEOPLE_STEP,
    },
    { page: 'box', path: SKIP_PATHS.BOX, status: Statuses.BOX_STEP },
    { page: 'billing', path: SKIP_PATHS.BILLING, status: Statuses.BILLING_STEP },
    { page: 'thankyou', path: SKIP_PATHS.THANK_YOU, status: Statuses.THANK_YOU_STEP },
  ];

  const logOutToBrand = [
    {
      page: 'Home Chef',
      onSubmit: () => handleSwitchBrands(RequestService.exitTempo, '/signup/intro/standard'),
    },
    {
      page: 'Tempo',
      onSubmit: () => handleSwitchBrands(RequestService.enterTempo, '/signup/intro/tempo'),
    },
    {
      page: 'Family',
      onSubmit: () =>
        handleSwitchBrands(RequestService.exitTempo, '/signup/intro/family?family=true'),
    },
  ];

  const LogOutButtons = () =>
    logOutToBrand.map(({ page, onSubmit }) => (
      <InternalSkipButton key={page} funnelPage={page} onSubmit={onSubmit} logout={true} />
    ));

  const updateUser = useCallback(
    async user => {
      const userResponse = await RequestService.updateUser({
        params: {
          name: user?.name || parseNameFromEmail(user) || generateRandomName('full'),
          fullName: user?.fullName || parseNameFromEmail(user) || generateRandomName('full'),
          address: {
            streetAddress: user?.streetAddress || generateChicagoStreetAddress(),
          },
          phone: user?.phone || generateChicagoPhoneNumber(),
        },
      });
      if (userResponse?.ok) {
        const updatedUser = {
          ...userResponse.user,
          ...user,
          address: {
            ...userResponse.user.address,
            ...user.address,
          },
        };

        setUser(updatedUser);
        return updatedUser;
      }
    },
    [setUser]
  );

  const handleBoxOrBillingPath = useCallback(
    async (user, thankYouPageRequest = false) => {
      let servings = isTempo ? 8 : 2;
      let weeklyMeals = isTempo ? 6 : user.weeklyMeals;

      if (isTempo) {
        // We dont need to do anything with the response. We just need to make the request so the box page loads on tempo.
        // eslint-disable-next-line no-unused-vars
        const boxAnswers = await RequestService.updateBoxSignupAnswerSelections({
          params: { box_recommendation: { answerIds: [8] } },
        });
      }

      const response = await RequestService.updateBox({
        params: { servings: servings, weeklyMeals: weeklyMeals },
      });

      if (response?.ok) {
        const billingPageUser = response.user;

        setUser(billingPageUser);
        setBasket({
          servings: billingPageUser.servings,
          meals: billingPageUser.weeklyMeals,
        });
        if (thankYouPageRequest) {
          return billingPageUser;
        }
      }
    },
    [isTempo, setBasket, setUser]
  );

  const handleThankYouPath = useCallback(
    async user => {
      const billingPageUser = await handleBoxOrBillingPath(user, true);

      const paymentParams = {
        paymentMethodId: 'pm_card_visa',
        vendor: 'stripe',
        termsChecked: true,
        applePayEmail: '',
        paypalEmail: '',

        fullName: billingPageUser?.name || parseNameFromEmail(user) || generateRandomName('full'),
        streetAddress: billingPageUser?.streetAddress || generateChicagoStreetAddress(),
        apartmentNumber: '',
        city: billingPageUser?.signupCity,
        state: billingPageUser?.signupState,
        zipCode: billingPageUser?.signupZip,
        phoneNumber: billingPageUser?.phone || generateChicagoPhoneNumber(),
        firstDeliveryDate: generateFutureDeliveryDate(),
        weeklyMeals: billingPageUser?.weeklyMeals,
        servings: billingPageUser?.servings,
      };

      const response = await RequestService.createCheckout({ params: paymentParams });

      if (response?.ok) {
        const thankYouPageBasket = response.weeklyBasket;
        setBasket(thankYouPageBasket);

        const thankYouPageAccessToken = response.accessToken;
        const thankYouPageUser = response.user;
        thankYouPageUser.accessToken = thankYouPageAccessToken;
        setUser(thankYouPageUser);
      }
    },
    [handleBoxOrBillingPath, setBasket, setUser]
  );

  const handleSubmit = useCallback(
    async (to, status) => {
      setIsSubmitting(true);
      try {
        const values = internalValues();
        const params = introSubmitParams(values);
        const response = await RequestService.createWelcome({ params });
        if (response?.ok) {
          setIsSubmitting(false);
          setIsInternalVisible(true);
          const user = response.user;
          setVoucher(user.signupVoucher);

          const userWithName = await updateUser(user);

          if (to === SKIP_PATHS.THANK_YOU) {
            await handleThankYouPath(userWithName);
          } else if (to === SKIP_PATHS.BILLING || to === SKIP_PATHS.BOX) {
            await handleBoxOrBillingPath(userWithName);
          } else {
            let statusResponse = await RequestService.updateStatus({
              params: { status },
            });

            if (statusResponse?.ok) {
              const updatedUserWithStatus = {
                ...userWithName,
                status,
              };
              setUser(updatedUserWithStatus);
            }
          }
          handlePath(history, to);
        } else {
          setError(response.error);
        }
      } catch (error) {
        setIsSubmitting(false);
        ErrorLogging.captureException(error.message);
      }
    },
    [
      internalValues,
      introSubmitParams,
      setIsSubmitting,
      setIsInternalVisible,
      setUser,
      setVoucher,
      setError,
      handlePath,
      history,
      updateUser,
      handleThankYouPath,
      handleBoxOrBillingPath,
    ]
  );

  const SkipTo = () =>
    funnelPages.map(({ page, path, status }) => (
      <InternalSkipButton
        key={page}
        funnelPage={page}
        onSubmit={() => handleSubmit(path, status)}
      />
    ));

  const handleLogoutAndRedirect = useCallback(
    async path => {
      try {
        const response = await RequestService.logoutUser();
        if (response?.ok) {
          if (path) {
            window.location.replace(path);
          } else {
            window.location.reload();
            handlePath(history, SKIP_PATHS.INTRO);
          }
        }
      } catch (error) {
        ErrorLogging.captureException(error.message);
      }
    },
    [handlePath, history]
  );

  const handleSwitchBrands = useCallback(
    async (requestFunction, path = null) => {
      try {
        const response = await requestFunction();
        if (response?.ok) {
          handleLogoutAndRedirect(path);
        }
      } catch {
        // no op
      }
    },
    [handleLogoutAndRedirect]
  );

  return (
    <div className="relative">
      <div className={cn('fixed left-3 top-24 z-50 w-52 rounded-xl bg-white-100 shadow-md')}>
        <button
          type="button"
          className={cn(
            'w-full cursor-pointer px-4 py-2 text-center text-sm font-medium focus:outline-none',
            {
              'rounded-t-lg border-b': isInternalVisible,
              'rounded-xl': !isInternalVisible,
              'hover:bg-tempo-blue-70': isTempo,
              'hover:bg-keto-100': isFamilyPlan,
              'hover:bg-vegetarian-100': isHomeChef,
            }
          )}
          onClick={() => setIsInternalVisible(!isInternalVisible)}
        >
          {isInternalVisible ? 'Hide Skip Feature' : 'Show Skip Feature'}
        </button>
        {isInternalVisible &&
          (user?.email ? (
            <div className="p-4">
              <p className="mb-1 text-center text-xs">Logged in as</p>
              <p className="mb-2 text-center text-xxs">{user.email}</p>
              <LogOutButtons />
            </div>
          ) : (
            <div className="p-4">
              <SkipTo funnelPages={funnelPages} handleSubmit={handleSubmit} />
            </div>
          ))}
      </div>
    </div>
  );
};

SkipContainer.propTypes = {
  history: PropTypes.object,
  introSubmitParams: PropTypes.func,
  setError: PropTypes.func,
  setIsSubmitting: PropTypes.func,
  mealPlan: PropTypes.string.isRequired,
};

export default SkipContainer;
